import CheckmarkIconOrange from "@/assets/images/icons/checkmark_icon_orange.svg";
import React from "react";
import { Platform } from "react-native";
import { Image, SizableText, XStack, YStack } from "tamagui";
import { Lesson, UserLesson } from "@/types";
import { useResponsiveImageUrl } from "@/utils/imageUtils";
import { lessonThumbnailProgressPercent } from "@/utils/lessonProgressPercent";
import { getLessonStructuredTitleMeta } from "@/utils/lessonStructuredTitle";
import { LessonStructuredTitleBlock } from "./LessonStructuredTitleBlock";

// Pointer can hover (desktop web): reveal the remove button on hover only.
// Touch devices (native or mobile web) get it always visible.
const HAS_HOVER =
  Platform.OS === "web" &&
  typeof window !== "undefined" &&
  !!window.matchMedia?.("(hover: hover)").matches;

interface LessonCardProps {
  item: Lesson;
  userLesson?: UserLesson;
  onPress?: () => void;
  /** When set, shows a ✕ over the thumbnail (e.g. remove from Continue watching). */
  onRemove?: () => void;
  /** Pass from useLessonCardLayout() — avoids a useMedia() call per card. */
  cardWidth?: number;
  progressBarHeight?: number;
  weekFormat?: "long" | "short";
}

export const LessonCard = React.memo(function LessonCard({
  item,
  userLesson,
  onPress,
  onRemove,
  cardWidth = 160,
  progressBarHeight = 3,
  weekFormat = "short",
}: LessonCardProps) {
  const [hovered, setHovered] = React.useState(false);
  const progress = userLesson
    ? lessonThumbnailProgressPercent({
        userProgress: userLesson.progress,
        videoCount: item.video_count,
        completedVideoCount: userLesson.completed_video_count,
        leftOffAtSeconds: userLesson.left_off_at_seconds,
        durationSeconds: item.duration,
      })
    : (item.progressPercentage ?? 0);

  const responsiveImageUrl = useResponsiveImageUrl(
    item.thumbnail || "",
    "card",
  );
  const { displayTitle, metaLine, mainWorkTitle, weekLabel } =
    getLessonStructuredTitleMeta(item, { weekFormat });

  const cardHeight = (cardWidth * 9) / 16;

  return (
    <YStack
      w={cardWidth}
      br={10}
      overflow="hidden"
      borderColor="#e0e0e0"
      backgroundColor="#fff"
      style={{ cursor: "pointer" }}
      onPress={onPress}
      onMouseEnter={
        onRemove && HAS_HOVER ? () => setHovered(true) : undefined
      }
      onMouseLeave={
        onRemove && HAS_HOVER ? () => setHovered(false) : undefined
      }
      hoverStyle={{
        scale: 1.03,
        shadowColor: "#000",
        shadowOpacity: 0.2,
        shadowRadius: 10,
      }}
      pressStyle={{ scale: 0.98, opacity: 0.9 }}
      animation="quick"
    >
      <YStack
        h={cardHeight}
        w="100%"
        borderTopLeftRadius={10}
        borderTopRightRadius={10}
        overflow="hidden"
        position="relative"
      >
        {responsiveImageUrl ? (
          <Image
            source={{ uri: responsiveImageUrl }}
            w="100%"
            h="100%"
            resizeMode="cover"
          />
        ) : (
          <YStack
            w="100%"
            h="100%"
            backgroundColor="#b0d0ff"
            justifyContent="center"
            alignItems="center"
          />
        )}
        {onRemove && (!HAS_HOVER || hovered) ? (
          <YStack
            position="absolute"
            top={6}
            right={6}
            w={24}
            h={24}
            br={12}
            bg="rgba(0,0,0,0.55)"
            jc="center"
            ai="center"
            zIndex={2}
            accessibilityLabel="Remove from Continue watching"
            style={{ cursor: "pointer" }}
            hoverStyle={{ bg: "rgba(0,0,0,0.8)" }}
            pressStyle={{ opacity: 0.8 }}
            onPress={(e: any) => {
              e?.stopPropagation?.();
              onRemove();
            }}
          >
            <SizableText color="#fff" fontSize={12} lineHeight={14}>
              ✕
            </SizableText>
          </YStack>
        ) : null}
        {userLesson?.is_completed && (
          <YStack
            position="absolute"
            top={9}
            right={9}
            justifyContent="center"
            alignItems="center"
            zIndex={1}
          >
            <CheckmarkIconOrange width={16} height={16} />
          </YStack>
        )}
      </YStack>

      <XStack h={progressBarHeight} backgroundColor="#D2CCC5" overflow="hidden">
        <YStack
          h="100%"
          backgroundColor="$accent"
          borderRadius={30}
          w={`${progress}%`}
        />
      </XStack>

      <YStack
        px={10}
        py={6}
        $gtSm={{ py: 10 }}
        backgroundColor="#fff"
        borderBottomLeftRadius={10}
        borderBottomRightRadius={10}
        shadowColor="#000"
        shadowOffset={{ width: 0, height: 4 }}
        shadowOpacity={0.15}
        shadowRadius={6}
        elevation={5}
      >
        <LessonStructuredTitleBlock
          displayTitle={displayTitle}
          metaLine={metaLine}
          metaPrefix={mainWorkTitle}
          metaSuffix={weekLabel}
          titleColor={
            item.course_title && item.title === item.course_title
              ? "#987451"
              : "$color12"
          }
          variant="card"
          useDesktopLayout={weekFormat === "long"}
        />
      </YStack>
    </YStack>
  );
});
